import { Button } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router';
import { useWizard } from 'react-use-wizard';

import * as orgRoutes from 'src/app/account/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';

import { SupergraphOnboardingStep, SupergraphOnboardingSteps } from './shared';

const CancelButton = ({
  creatingSupergraph,
}: {
  creatingSupergraph: boolean;
}) => {
  const { activeStep } = useWizard();
  const activeStepName = SupergraphOnboardingSteps[activeStep];
  const history = useHistory();
  const { orgId } = useRouteParams(orgRoutes.supergraphOnboarding);
  return (
    <Button
      data-analytics-label={`button:Cancel,currentStep:${activeStepName}`}
      data-analytics-category="6M2S: Onboarding"
      data-testid="supergraph-onboarding-cancel-button"
      size="lg"
      variant="secondary"
      type="button"
      isDisabled={creatingSupergraph}
      onClick={() => {
        history.replace({
          ...orgRoutes.graphs.location({ orgId }),
        });
      }}
    >
      Cancel
    </Button>
  );
};

const BackButton = ({
  className,
  creatingSupergraph,
  additionalOnClickEffects,
}: {
  className?: string;
  creatingSupergraph: boolean;
  additionalOnClickEffects?: () => void;
}) => {
  const { previousStep, activeStep } = useWizard();
  const activeStepName = SupergraphOnboardingSteps[activeStep];
  return (
    <Button
      data-analytics-label={`button:Back,currentStep:${activeStepName}`}
      data-analytics-category="6M2S: Onboarding"
      data-testid="supergraph-onboarding-back-button"
      isDisabled={creatingSupergraph}
      className={classnames('mr-4', className)}
      size="lg"
      type="button"
      variant="secondary"
      onClick={() => {
        additionalOnClickEffects?.();
        previousStep();
      }}
    >
      Back
    </Button>
  );
};

export type ButtonState = {
  isDisabled: boolean;
  disabledReason: string | undefined;
};

const NextButton = ({
  creatingSupergraph,
  loading,
  continueAnalyticsLabelsByStep,
  whenStepsBegan,
  smallScreen,
  state,
}: {
  state: ButtonState;
  creatingSupergraph: boolean;
  loading?: boolean;
  continueAnalyticsLabelsByStep?: Record<SupergraphOnboardingStep, string>;
  whenStepsBegan: Partial<Record<SupergraphOnboardingStep, number>> | undefined;
  smallScreen: boolean;
}) => {
  const { activeStep, nextStep } = useWizard();
  const activeStepName = SupergraphOnboardingSteps[activeStep];

  const trackCustomEvent = useTrackCustomEvent();

  /**
   * We use trackCustomEvent directly here instead of adding data-analytics params to the
   * Button element because we want to record the time this button was clicked
   */
  const handleOnClick = useCallback(() => {
    const startTimeOfThisStep =
      activeStepName && whenStepsBegan?.[activeStepName];
    trackCustomEvent({
      action: 'Click',
      category: '6M2S: Onboarding',
      secondaryLabel:
        activeStepName && continueAnalyticsLabelsByStep?.[activeStepName]
          ? `,${continueAnalyticsLabelsByStep[activeStepName]}`
          : '',
      label: `button:Next,currentStep:${activeStepName}${
        startTimeOfThisStep
          ? `,timeInMsOnThisStep:${Date.now() - startTimeOfThisStep}`
          : ''
      }`,
    });
    nextStep();
  }, [
    activeStepName,
    continueAnalyticsLabelsByStep,
    nextStep,
    trackCustomEvent,
    whenStepsBegan,
  ]);

  return (
    <Tooltip
      disabled={!(state.isDisabled || creatingSupergraph)}
      label={state.disabledReason}
      placement="left"
    >
      <Button
        data-testid={`supergraph-onboarding-next-button-${activeStepName?.replaceAll(
          ' ',
          '-',
        )}${smallScreen ? '-small' : ''}`}
        size="lg"
        type="submit"
        isDisabled={state.isDisabled || creatingSupergraph}
        isLoading={loading}
        loadingText="Loading"
        onClick={handleOnClick}
      >
        Next
      </Button>
    </Tooltip>
  );
};

const CreateGraphButton = ({
  creatingSupergraph,
  onClick,
  continueAnalyticsLabelsByStep,
  whenStepsBegan,
  smallScreen,
  state,
}: {
  creatingSupergraph: boolean;
  state: ButtonState;
  onClick: () => void;
  continueAnalyticsLabelsByStep: Record<SupergraphOnboardingStep, string>;
  whenStepsBegan: Partial<Record<SupergraphOnboardingStep, number>> | undefined;
  smallScreen: boolean;
}) => {
  const { includeVariantOnboardingStep } = useRouteParams(
    orgRoutes.supergraphOnboarding,
  );
  const isVariantOnboardingFlow = includeVariantOnboardingStep === 'true';
  const trackCustomEvent = useTrackCustomEvent();
  /**
   * We use trackCustomEvent directly here instead of adding data-analytics params to the
   * Button element because we want to record the time this button was clicked
   */
  const handleOnClick = useCallback(() => {
    const startTimeOfThisStep =
      whenStepsBegan?.[SupergraphOnboardingStep.Supergraph];
    trackCustomEvent({
      action: 'Click',
      category: '6M2S: Onboarding',
      secondaryLabel: continueAnalyticsLabelsByStep?.[
        SupergraphOnboardingStep.Supergraph
      ]
        ? `,${
            continueAnalyticsLabelsByStep[SupergraphOnboardingStep.Supergraph]
          }`
        : '',

      label: `button:Create Supergraph${
        startTimeOfThisStep
          ? `,timeInMsOnThisStep:${Date.now() - startTimeOfThisStep}`
          : ''
      }`,
    });
    onClick();
  }, [
    continueAnalyticsLabelsByStep,
    onClick,
    trackCustomEvent,
    whenStepsBegan,
  ]);

  return (
    <Tooltip
      disabled={!state.isDisabled}
      label={state.disabledReason}
      placement="left"
    >
      <Button
        data-testid={`create-supergraph-button${smallScreen ? '-small' : ''}`}
        size="lg"
        type="submit"
        isLoading={creatingSupergraph}
        loadingText={
          <span className="italic">
            Creating{' '}
            <span className="hidden lg:inline 3xl:hidden">
              {' '}
              your{' '}
              {!isVariantOnboardingFlow ? 'GraphOS Router' : 'GraphOS Variant'}
            </span>
          </span>
        }
        isDisabled={state.isDisabled || creatingSupergraph}
        onClick={handleOnClick}
      >
        Create {!isVariantOnboardingFlow ? 'GraphOS Router' : 'GraphOS Variant'}
      </Button>
    </Tooltip>
  );
};

export const NewSupergraphOnboardingControls = {
  CancelButton,
  NextButton,
  BackButton,
  CreateGraphButton,
};
